import { useContext } from 'react';
import PropTypes from 'prop-types';
import { RedirectTag, useHelmet } from '@thd-nucleus/thd-helmet';
import {
  number,
  params,
  shape,
  string,
  useDataModel
} from '@thd-nucleus/data-sources';
import { RedirectContext } from './RedirectContext';
import {
  isInvalidQuestionsCanonical,
  isInvalidPageNumber,
  isInvalidItemId,
  isNotFound
} from './rules/question-redirect-rules';
import { handleErrors, ERROR_HANDLING_EXPERIENCE } from '../utils/redirector-utils';

const questionsHandler = ({ data, error, props }) => {
  const { itemId, pageNumber, bypass, path } = props;
  const { productError, questionsError } = error;
  const { productData, questionsData } = data;

  const invalidItemId = isInvalidItemId({ itemId });
  if (invalidItemId) {
    return new RedirectTag(invalidItemId, bypass);
  }

  const productErrors = productError ? handleErrors(productError, ERROR_HANDLING_EXPERIENCE.PRODUCT) : null;
  if (productErrors) {
    return new RedirectTag(productErrors, bypass);
  }

  const notFound = isNotFound({ data: productData, itemId });
  if (notFound) {
    return new RedirectTag(notFound, bypass);
  }

  const invalidPageNumber = isInvalidPageNumber({ data: questionsData, pageNumber, pageSize: 8 });
  if (invalidPageNumber) {
    return new RedirectTag(invalidPageNumber, bypass);
  }

  const badCanonicalShouldRedirect = isInvalidQuestionsCanonical({ data: productData, path });
  if (badCanonicalShouldRedirect) {
    return new RedirectTag(badCanonicalShouldRedirect, bypass);
  }

  return null;
};

export const QuestionsRedirector = ({ itemId }) => {
  const { pathWithQueryParams, bypass } = useContext(RedirectContext);
  const [path] = pathWithQueryParams?.split('?') || '';

  const opts = {
    variables: {
      itemId
    },
    skip: !itemId
  };

  const segments = path?.replace(/^\/|\/$/g, '').split('/');
  const pageNumber = segments?.length > 4 ? segments[segments.length - 1] : 1;
  // eslint-disable-next-line no-restricted-globals
  const skipQuestions = !itemId || isNaN(pageNumber) || Number(pageNumber) < 1;
  const questionsOptions = {
    variables: {
      itemId
    },
    skip: skipQuestions
  };

  const props = {
    itemId,
    bypass,
    pathWithQueryParams,
    pageNumber,
    path
  };

  const { data: productData, error: productError } = useDataModel('product', opts);
  const { data: questionsData, error: questionsError } = useDataModel('questionsAnswers', questionsOptions);

  const data = { productData, questionsData };
  const error = { productError, questionsError };

  useHelmet('questionsRedirector', { props, data, error }, questionsHandler, [itemId, data, error]);

  return null;
};

QuestionsRedirector.displayName = 'QuestionsRedirector';

QuestionsRedirector.propTypes = {
  itemId: PropTypes.string.isRequired
};

QuestionsRedirector.defaultProps = {};

QuestionsRedirector.dataModel = {
  product: params({ itemId: string().isRequired() }).shape({
    itemId: string(),
    dataSources: string(),
    identifiers: shape({
      canonicalUrl: string()
    })
  }),
  questionsAnswers: params({
    itemId: string().isRequired()
  }).shape({
    TotalResults: number(),
    Offset: number()
  })
};